import type { IProduct, IProductToSave } from '@/types/product';
import { formatCurrency, normalizeCurrency } from '@/locale/format';
import { formatDate } from '@/filters/dateFormat';
import { extractSelectedProductOptions } from './functions';

import { defaultCurrency } from '@/config/app';
import translator from '@/locale/translator';

/**
 * !!!NOTE by Mario: I just need a quick and dirty way to disply the cart products,
 * i did not think any further then trying to get the demo to work, so please feel free to completely rewrite
 * replace this.
 */

export interface ProductPriceInfo {
  basePrice: number;
  optionsCost: number;
  totalPrice: number;
  description: string;
  currency: string;
}

export interface ProductDescriptionResult {
  description: string;
  hasPrice: boolean;
}

/**
 * Finds a product from the selected products array by ID
 */
function findSelectedProduct(productId: number, selectedProducts: IProduct[]): IProduct | undefined {
  return selectedProducts.find((product) => product.id === productId);
}

/**
 * Calculates the base price for a product (budget or fixed price)
 */
function calculateBasePrice(
  productToSave: IProductToSave,
  selectedProducts: IProduct[]
): { price: number; description: string; currency: string } {
  // variable budget price
  if (productToSave.budget) {
    return {
      price: productToSave.budget,
      description: formatCurrency(productToSave.budget, productToSave.currency || defaultCurrency),
      currency: productToSave.currency || defaultCurrency,
    };
  }

  // fixed price with options
  const selectedProduct = findSelectedProduct(productToSave.id, selectedProducts);
  if (selectedProduct?.price) {
    const currency = normalizeCurrency(productToSave.currency || defaultCurrency);
    return {
      price: selectedProduct.price,
      description: `Fixed price - ${formatCurrency(selectedProduct.price, currency)}`,
      currency,
    };
  }

  return { price: 0, description: '', currency: productToSave.currency || defaultCurrency };
}

/**
 * Calculates the cost of selected options for a product
 */
function calculateOptionsCost(
  productToSave: IProductToSave,
  selectedProducts: IProduct[]
): { cost: number; description: string; currency: string } {
  if (!productToSave.selectedOptions || Object.keys(productToSave.selectedOptions).length === 0) {
    return { cost: 0, description: '', currency: productToSave.currency || defaultCurrency };
  }

  const selectedProduct = findSelectedProduct(productToSave.id, selectedProducts);
  if (!selectedProduct?.product_options?.data) {
    return { cost: 0, description: '', currency: productToSave.currency || defaultCurrency };
  }

  const selectedOptions = extractSelectedProductOptions(
    productToSave.selectedOptions,
    selectedProduct.product_options.data
  );

  const totalCost = selectedOptions.reduce((total, option) => total + (option.cost || 0), 0);
  // options follow the product currency; their own cost_currency is only a fallback (often unset)
  const currency =
    productToSave.currency || (selectedOptions.length > 0 ? selectedOptions[0].currency : undefined) || defaultCurrency;

  if (totalCost > 0) {
    const optionsText = selectedOptions.map((option) => option.title).join(', ');
    return {
      cost: totalCost,
      description: `${optionsText}\n${formatCurrency(totalCost, currency)}`,
      currency,
    };
  }

  return { cost: 0, description: '', currency: defaultCurrency };
}

/**
 * Creates a description for products with special configurations (metric goals, date ranges)
 */
function createSpecialProductDescription(
  productToSave: IProductToSave,
  priceInfo: ProductPriceInfo
): ProductDescriptionResult {
  const hasPrice = priceInfo.totalPrice > 0;

  if (productToSave.metric_goal) {
    const baseDescription = `${translator.get('count')}: ${productToSave.metric_goal} ${translator.get('spending')}: ${formatCurrency(productToSave.spending_limit || 0, priceInfo.currency)}`;
    return {
      description: hasPrice
        ? `${baseDescription}\n${formatCurrency(priceInfo.totalPrice, priceInfo.currency)}`
        : baseDescription,
      hasPrice,
    };
  }

  if (productToSave.startdate && productToSave.enddate) {
    const baseDescription = `${translator.get('from')} ${formatDate(productToSave.startdate)} ${translator.get('to')} ${formatDate(productToSave.enddate)}`;
    return {
      description: hasPrice
        ? `${baseDescription}\n${formatCurrency(priceInfo.totalPrice, priceInfo.currency)}`
        : baseDescription,
      hasPrice,
    };
  }

  return { description: '', hasPrice: false };
}

/**
 * Creates a fallback description for products with options but no calculated price
 */
function createOptionsFallbackDescription(productToSave: IProductToSave, selectedProducts: IProduct[]): string {
  if (!productToSave.selectedOptions) return '';

  const selectedProduct = findSelectedProduct(productToSave.id, selectedProducts);
  if (!selectedProduct?.product_options?.data) return '';

  const selectedOptions = extractSelectedProductOptions(
    productToSave.selectedOptions,
    selectedProduct.product_options.data
  );

  return selectedOptions.map((option) => option.title).join(', ');
}

/**
 * Calculates the complete price information for a product in the cart
 */
export function calculateProductPrice(productToSave: IProductToSave, selectedProducts: IProduct[]): ProductPriceInfo {
  if (!productToSave.id) {
    return { basePrice: 0, optionsCost: 0, totalPrice: 0, description: '', currency: defaultCurrency };
  }

  const {
    price: basePrice,
    description: baseDescription,
    currency: baseCurrency,
  } = calculateBasePrice(productToSave, selectedProducts);
  const {
    cost: optionsCost,
    description: optionsDescription,
    currency: optionsCurrency,
  } = calculateOptionsCost(productToSave, selectedProducts);

  const selectedProduct = findSelectedProduct(productToSave.id, selectedProducts);

  const totalPrice =
    optionsCost > 0 ? (selectedProduct?.type !== 'single' ? optionsCost + basePrice : optionsCost) : basePrice;
  const finalCurrency = optionsCost > 0 ? optionsCurrency : baseCurrency;
  const description =
    optionsDescription || baseDescription || (totalPrice > 0 ? formatCurrency(totalPrice, finalCurrency) : '');

  return {
    basePrice,
    optionsCost,
    totalPrice,
    description,
    currency: finalCurrency,
  };
}

/**
 * Calculates the total price for all products in the cart
 */
export function calculateCartTotal(
  productsToSave: IProductToSave[],
  selectedProducts: IProduct[]
): Record<string, number> {
  return productsToSave.reduce(
    (totals, product) => {
      const priceInfo = calculateProductPrice(product, selectedProducts);
      const currency = priceInfo.currency;

      if (!totals[currency]) {
        totals[currency] = 0;
      }
      totals[currency] += priceInfo.totalPrice;
      return totals;
    },
    {} as Record<string, number>
  );
}

/**
 * Collapses per-currency cart subtotals into a single total expressed in the target currency.
 *
 * Subtotals already in the target currency are added as-is; the rest are converted through the
 * injected `convert` function (the /convert api in production). Zero subtotals are skipped so no
 * pointless conversion request is made. A conversion that fails is logged and left out of the total.
 */
export async function sumCartTotalInCurrency(
  totals: Record<string, number>,
  targetCurrency: string,
  convert: (amount: number, fromCurrency: string, toCurrency: string) => Promise<{ amount: number }>
): Promise<number> {
  let total = 0;
  const conversions: Promise<{ amount: number }>[] = [];

  for (const currency of Object.keys(totals)) {
    if (totals[currency] === 0) continue;

    if (currency !== targetCurrency) {
      conversions.push(convert(totals[currency], currency, targetCurrency));
    } else {
      total += totals[currency];
    }
  }

  // a failed conversion drops only that subtotal instead of losing the whole total
  const results = await Promise.allSettled(conversions);
  for (const result of results) {
    if (result.status === 'fulfilled') total += result.value.amount;
    else console.error('Currency conversion failed:', result.reason);
  }

  return total;
}

export function calculateProductTotal(productToSave: IProductToSave, selectedProducts: IProduct[]) {
  const priceInfo = calculateProductPrice(productToSave, selectedProducts);
  const currency = priceInfo.currency;

  return `${formatCurrency(priceInfo.totalPrice, currency)}`;
}

/**
 * Generates a description for a product in the cart
 */
export function getProductCartDescription(productToSave: IProductToSave, selectedProducts: IProduct[]): string {
  if (!productToSave.id) return '';

  const priceInfo = calculateProductPrice(productToSave, selectedProducts);
  const specialDescription = createSpecialProductDescription(productToSave, priceInfo);

  if (specialDescription.description) {
    return specialDescription.description;
  }

  if (priceInfo.totalPrice > 0) {
    return priceInfo.description;
  }
  return createOptionsFallbackDescription(productToSave, selectedProducts);
}
